// Active Builds: client builds and their pages/screens (migration
// 20261103140000_active_builds.sql). A page is backlog -> queued (released onto
// the Fulfillment board as a client_task) -> done (first draft shipped). The
// daily scheduler releases the top backlog page on each day the build's cadence
// lands on; "Release next" does the same by hand.

import { supabase } from './supabase';

export type BuildFreq = 'daily' | 'weekly' | 'monthly';
export type BuildStatus = 'active' | 'paused' | 'finished';
export type PageStatus = 'backlog' | 'queued' | 'done';
export type PageKind = 'page' | 'screen';

export interface ActiveBuild {
  id: string;
  client_id: string;
  name: string;
  notes: string | null;
  status: BuildStatus;
  assigned_to: string | null;
  freq: BuildFreq;
  interval_n: number;
  byweekday: number[] | null;
  bymonthday: number | null;
  start_date: string;
  hold_while_open: boolean;
  auto_release: boolean;
  released_through: string;
  seed_source: string | null;
  seed_evidence: string | null;
  created_at: string;
  client?: { name: string } | null;
}

export interface BuildPage {
  id: string;
  build_id: string;
  title: string;
  path: string | null;
  kind: PageKind;
  status: PageStatus;
  position: number;
  note: string | null;
  task_id: string | null;
  released_at: string | null;
  done_at: string | null;
  task?: { id: string; status: string | null; due_date: string | null } | null;
}

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function cadenceLabel(b: Pick<ActiveBuild, 'freq' | 'interval_n' | 'byweekday' | 'bymonthday' | 'start_date'>): string {
  const n = b.interval_n;
  if (b.freq === 'daily') return n > 1 ? `Every ${n} days` : 'Every day';
  if (b.freq === 'weekly') {
    const days = (b.byweekday ?? []).slice().sort((a, c) => a - c).map((d) => DAY_LABELS[d]).join(', ');
    const base = n > 1 ? `Every ${n} weeks` : 'Weekly';
    return days ? `${base} on ${days}` : base;
  }
  const dom = b.bymonthday ?? Number(b.start_date.slice(8, 10));
  return `${n > 1 ? `Every ${n} months` : 'Monthly'} on day ${dom}`;
}

/** Pages per week the cadence releases, for the "backlog clears in ~N weeks" hint. */
export function pagesPerWeek(b: Pick<ActiveBuild, 'freq' | 'interval_n' | 'byweekday'>): number {
  if (b.freq === 'daily') return 7 / b.interval_n;
  if (b.freq === 'weekly') return Math.max(1, b.byweekday?.length ?? 1) / b.interval_n;
  return 12 / 52 / b.interval_n;
}

export async function fetchBuilds(): Promise<ActiveBuild[]> {
  const { data, error } = await supabase
    .from('active_builds')
    .select('*, client:clients(name)')
    .order('created_at');
  if (error) throw error;
  return (data as unknown as ActiveBuild[]) ?? [];
}

export async function fetchPages(buildId: string): Promise<BuildPage[]> {
  const { data, error } = await supabase
    .from('active_build_pages')
    .select('*, task:client_tasks(id, status, due_date)')
    .eq('build_id', buildId)
    .order('position')
    .order('created_at');
  if (error) throw error;
  return (data as unknown as BuildPage[]) ?? [];
}

export async function saveBuild(id: string | null, patch: Partial<ActiveBuild>): Promise<string> {
  const { client: _client, ...row } = patch;
  void _client;
  if (id) {
    const { error } = await supabase.from('active_builds').update(row).eq('id', id);
    if (error) throw error;
    return id;
  }
  if (!row.client_id) throw new Error('A build needs a client');
  const { data, error } = await supabase
    .from('active_builds')
    .insert({ ...row, client_id: row.client_id })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function deleteBuild(id: string): Promise<void> {
  const { error } = await supabase.from('active_builds').delete().eq('id', id);
  if (error) throw error;
}

export async function addPage(buildId: string, title: string, path: string | null, kind: PageKind, position: number): Promise<void> {
  const { error } = await supabase
    .from('active_build_pages')
    .insert({ build_id: buildId, title, path, kind, position, status: 'backlog' });
  if (error) throw error;
}

export async function updatePage(id: string, patch: Partial<Pick<BuildPage, 'title' | 'path' | 'kind' | 'status' | 'note' | 'position'>>): Promise<void> {
  const { error } = await supabase.from('active_build_pages').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deletePage(id: string): Promise<void> {
  const { error } = await supabase.from('active_build_pages').delete().eq('id', id);
  if (error) throw error;
}

/** Rewrite positions to match the given order (backlog reorder). */
export async function reorderPages(ids: string[]): Promise<void> {
  const results = await Promise.all(
    ids.map((id, i) => supabase.from('active_build_pages').update({ position: i }).eq('id', id)),
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw failed.error;
}

/** Release the top backlog page onto the Fulfillment board now. Returns the task id, or null when the backlog is empty. */
export async function releaseNext(buildId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('release_build_page', { p_build_id: buildId });
  if (error) throw error;
  return (data as string | null) ?? null;
}

/** done / queued / backlog per build, for the rail. */
export async function fetchPageCounts(): Promise<Record<string, { done: number; queued: number; backlog: number }>> {
  const { data, error } = await supabase.from('active_build_pages').select('build_id, status').range(0, 9999);
  if (error) throw error;
  const out: Record<string, { done: number; queued: number; backlog: number }> = {};
  for (const r of (data ?? []) as Array<{ build_id: string; status: PageStatus }>) {
    const c = (out[r.build_id] ??= { done: 0, queued: 0, backlog: 0 });
    c[r.status] += 1;
  }
  return out;
}
